import React from 'react';
import PropTypes from 'prop-types';
import { Empty } from 'antd';
import './KPIWidget.scss';

const KPIWidget = ({ data }) => {
  if (!data || data.valor === undefined) {
    return <Empty description="No hay datos disponibles" />;
  }

  // Formatear valor
  const formatValue = (value) => {
    const { formato } = data;
    if (!formato) return value;

    let formattedValue = value;

    if (typeof value === 'number' && formato.decimales !== undefined) {
      formattedValue = value.toFixed(formato.decimales);
    }

    if (formato.prefijo) {
      formattedValue = `${formato.prefijo}${formattedValue}`;
    }

    if (formato.sufijo) {
      formattedValue = `${formattedValue}${formato.sufijo}`;
    }

    return formattedValue;
  };

  return (
    <div className="kpi-widget">
      {/* Timestamp en la esquina superior derecha */}
      {data.timestamp && (
        <div className="kpi-timestamp">
          {new Date(data.timestamp).toLocaleTimeString('es-MX', { 
            hour: '2-digit', 
            minute: '2-digit' 
          })}
        </div>
      )}

      {/* Header: Título + Período en una sola línea */}
      <div className="kpi-header">
        <span className="kpi-title">CONSUMO HOY</span>
        {data.estadisticas?.ventanaTiempo && (
          <span className="kpi-periodo">
            {(() => {
              if (data.estadisticas.ventanaTiempo === '1d') return 'vs Ayer';
              if (data.estadisticas.ventanaTiempo === '7d') return 'vs 7d';
              if (data.estadisticas.ventanaTiempo === '15d') return 'vs 15d';
              if (data.estadisticas.ventanaTiempo === '30d') return 'vs 30d';
              return '';
            })()}
          </span>
        )}
      </div>

      {/* Valor principal ultra compacto */}
      <div className="kpi-main-value">
        <div className="kpi-value-number">
          {data.valor.toFixed(data.formato?.decimales || 2)}
        </div>
        <div className="kpi-value-unit">
          {data.formato?.sufijo || data.unidades}
        </div>
      </div>

      {/* Comparación inline ultra compacta */}
      {data.estadisticas && data.estadisticas.promedioHistorico !== undefined && (
        <div className="kpi-comparison-inline">
          {(() => {
            const diferencia = data.valor - data.estadisticas.promedioHistorico;
            const porcentaje = (diferencia / data.estadisticas.promedioHistorico) * 100;
            const color = diferencia > 0 ? '#f5222d' : '#52c41a';
            const icon = diferencia > 0 ? '▲' : '▼';
            
            // Determinar texto del período desde estadisticas.ventanaTiempo
            let textoPeriodo = 'últimos 30 días'; // default
            const ventana = data.estadisticas.ventanaTiempo;
            if (ventana === '1d') textoPeriodo = 'ayer';
            else if (ventana === '7d') textoPeriodo = 'últimos 7 días';
            else if (ventana === '15d') textoPeriodo = 'últimos 15 días';
            else if (ventana === '30d') textoPeriodo = 'últimos 30 días';
            
            return (
              <>
                <div className="comparison-main" style={{ color }}>
                  <span className="comparison-icon">{icon}</span>
                  <span className="comparison-percent">{Math.abs(porcentaje).toFixed(1)}%</span>
                  <span className="comparison-vs">vs {textoPeriodo}</span>
                </div>
                <div className="comparison-detail">
                  <span className="detail-item">
                    Hoy: <strong>{formatValue(data.valor)}</strong>
                  </span>
                  <span className="detail-separator">•</span>
                  <span className="detail-item">
                    Prom: <strong>{formatValue(data.estadisticas.promedioHistorico)}</strong>
                  </span>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Stats mínimo/máximo inline */}
      {data.estadisticas && (data.estadisticas.minimoHistorico !== undefined || data.estadisticas.maximoHistorico !== undefined) && (
        <div className="kpi-stats-inline">
          {data.estadisticas.minimoHistorico !== undefined && (
            <div className="stat-inline-item">
              <span className="stat-label">Mín</span>
              <span className="stat-value" style={{ color: '#52c41a' }}>
                {formatValue(data.estadisticas.minimoHistorico)}
              </span>
            </div>
          )}
          {data.estadisticas.minimoHistorico !== undefined && data.estadisticas.maximoHistorico !== undefined && (
            <span className="stat-separator">|</span>
          )}
          {data.estadisticas.maximoHistorico !== undefined && (
            <div className="stat-inline-item">
              <span className="stat-label">Máx</span>
              <span className="stat-value" style={{ color: '#f5222d' }}>
                {formatValue(data.estadisticas.maximoHistorico)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

KPIWidget.propTypes = {
  data: PropTypes.shape({
    valor: PropTypes.number.isRequired, // Consumo de HOY
    unidades: PropTypes.string,
    timestamp: PropTypes.string,
    formato: PropTypes.shape({
      decimales: PropTypes.number,
      prefijo: PropTypes.string,
      sufijo: PropTypes.string,
    }),
    tendencia: PropTypes.shape({
      tipo: PropTypes.string,
      cambio: PropTypes.number,
      periodoComparacion: PropTypes.string,
    }),
    estadisticas: PropTypes.shape({
      promedioHistorico: PropTypes.number,   // Promedio histórico (sin incluir hoy)
      minimoHistorico: PropTypes.number,     // Mínimo histórico
      maximoHistorico: PropTypes.number,     // Máximo histórico
      diasComparacion: PropTypes.number,     // Número de días comparados
      ventanaTiempo: PropTypes.string,       // "1d", "7d", "15d", "30d"
    }),
  }).isRequired,
};

export default KPIWidget;
